import { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { useThree } from '@react-three/fiber'
import { ContactShadows, OrbitControls } from '@react-three/drei'
import { EffectComposer, N8AO, SMAA } from '@react-three/postprocessing'
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib'
import { ProductModel } from './ProductModel'
import type { Afmeting } from './ProductModel'
import { Studio } from './Studio'
import type { ProductConfig } from './types'

/** Seconden stilstand voordat het automatisch draaien weer begint. */
const RUST_VOOR_AUTODRAAI = 4

export interface SceneHandvat {
  herstel: () => void
}

/**
 * De 3D-kant van de viewer: model, licht, schaduw, camera en bediening.
 *
 * Alles wat met maat te maken heeft wordt afgeleid uit het opgemeten model en
 * niet uit vaste getallen. Dat is wat één viewer voor tien verschillende
 * machines mogelijk maakt.
 */
export function ProductScene({
  product, draait, onHandvat, onGeladen,
}: {
  product: ProductConfig & { modelUrl: string }
  draait: boolean
  onHandvat?: (h: SceneHandvat) => void
  onGeladen?: () => void
}) {
  const camera = useThree((s) => s.camera)
  const controls = useRef<OrbitControlsImpl>(null)
  const [maat, zetMaat] = useState<Afmeting | null>(null)
  const [bezig, zetBezig] = useState(false)

  const plaatsCamera = useCallback((a: Afmeting) => {
    const r = product.camera.richting
    const lengte = Math.hypot(r[0], r[1], r[2]) || 1
    const d = a.straal * product.camera.afstand
    const doel = new THREE.Vector3(0, a.hoogte * product.kijkhoogte, 0)
    camera.position.set(
      doel.x + (r[0] / lengte) * d,
      doel.y + (r[1] / lengte) * d,
      doel.z + (r[2] / lengte) * d,
    )
    // Clipvlakken meeschalen: op 0,1–220 (de magazijnwaarden) krijg je op een
    // model van twee meter zichtbare z-fighting in de spleten.
    camera.near = Math.max(0.01, a.straal * 0.02)
    camera.far = a.straal * 60
    camera.updateProjectionMatrix()
    if (controls.current) {
      controls.current.target.copy(doel)
      controls.current.update()
    }
  }, [camera, product.camera, product.kijkhoogte])

  const gemeten = useCallback((a: Afmeting) => {
    // Alleen bij een echte verandering: een nieuw object met dezelfde getallen is
    // voor React een verandering, en dat is precies hoe je hier een lus bouwt.
    zetMaat((vorig) =>
      vorig && Math.abs(vorig.straal - a.straal) < 1e-6 && Math.abs(vorig.hoogte - a.hoogte) < 1e-6
        ? vorig
        : a,
    )
    plaatsCamera(a)
    onGeladen?.()
  }, [plaatsCamera, onGeladen])

  useEffect(() => {
    onHandvat?.({ herstel: () => { if (maat) plaatsCamera(maat) } })
  }, [onHandvat, maat, plaatsCamera])

  // Automatisch draaien stopt zodra iemand het product vastpakt en komt pas
  // terug na een paar seconden rust. Dat is het verschil tussen "hij draait" en
  // "hij vecht met me".
  useEffect(() => {
    const c = controls.current
    if (!c) return
    let id: number | undefined
    const start = () => { zetBezig(true); window.clearTimeout(id) }
    const eind = () => {
      window.clearTimeout(id)
      id = window.setTimeout(() => zetBezig(false), RUST_VOOR_AUTODRAAI * 1000)
    }
    c.addEventListener('start', start)
    c.addEventListener('end', eind)
    return () => {
      window.clearTimeout(id)
      c.removeEventListener('start', start)
      c.removeEventListener('end', eind)
    }
  }, [maat])

  const straal = maat?.straal ?? 1


  return (
    <>
      <color attach="background" args={['#101216']} />
      <Studio product={product} />

      {/* Een zacht sleutellicht bovenop de omgeving, puur voor de slagschaduw:
          een environment map werpt zelf geen schaduw. */}
      <directionalLight
        position={[-straal * 3, straal * 5, straal * 3.5]}
        intensity={1.1}
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-bias={-0.0004}
        shadow-normalBias={straal * 0.01}
        shadow-camera-left={-straal * 1.6}
        shadow-camera-right={straal * 1.6}
        shadow-camera-top={straal * 1.6}
        shadow-camera-bottom={-straal * 1.6}
        shadow-camera-far={straal * 12}
      />

      <Suspense fallback={null}>
        <ProductModel product={product} onGemeten={gemeten} />
      </Suspense>

      {/* Contactschaduw in plaats van een vloervlak: het product staat ergens op
          zonder dat er een zichtbare plaat onder ligt. Zonder dit zweeft het. */}
      {maat && (
        <ContactShadows
          position={[0, 0.001, 0]}
          scale={straal * 3.4}
          resolution={1024}
          far={maat.hoogte * 0.9}
          blur={2.4}
          opacity={0.72}
          color="#05070b"
          frames={1}
        />
      )}

      <OrbitControls
        ref={controls}
        makeDefault
        enablePan={false}
        enableDamping
        dampingFactor={0.08}
        rotateSpeed={0.7}
        zoomSpeed={0.8}
        autoRotate={draait && product.autoDraaien && !bezig}
        autoRotateSpeed={0.55}
        // De camera kan niet door het model heen: de ondergrens is de omhullende
        // bol plus een marge. De bovengrens houdt het product beeldvullend.
        minDistance={straal * 1.15}
        maxDistance={straal * 5}
        // Niet onder de vloer door kunnen kijken, en niet recht van boven.
        minPolarAngle={0.18}
        maxPolarAngle={Math.PI / 2 - 0.04}
      />

      <EffectComposer multisampling={0} enableNormalPass>
        {/* Alleen occlusie en anti-aliasing. Geen bloom: die maakt van elk
            hooglicht een gloed, en dat is precies de game-look. */}
        <N8AO aoRadius={straal * 0.35} intensity={1.6} distanceFalloff={1} color="#05070c" />
        <SMAA />
      </EffectComposer>
    </>
  )
}
